import { Button, Card, Inline, Text, breakpoints, textVariants } from '@etg/wings';
import { css, cx, mediaQueries } from '@eti/styles';
import { useTranslation } from '@eti/providers';
import { variants, LoadingDots } from '@eti/components';
import { useState } from 'react';
import type { TicketType, TicketCardV4Props as TicketV4Props } from '../../model';
import { TicketPriceInfo } from '../common/TicketPriceInfo';
import {
  PRICE_MIN_THRESHOLD_LENGTH,
  TICKET_TITLE_MIN_THRESHOLD_LENGTH,
  TICKET_TYPES,
} from '../../constants';
import { priceInfoCommonStyles } from '../common/helper';
import { TicketPriceInfoWrapper } from './common/TicketPriceInfoWrapper';

const cardStyles = css`
  &:first-child {
    padding-bottom: 0;
  }

  &:last-child {
    padding-top: 0;
  }

  @media ${mediaQueries.small.only} {
    padding-left: 10px;
    padding-right: 10px;
  }
`;

const wrapperStyles = (index: number) => css`
  align-items: center;
  flex-wrap: nowrap;
  justify-content: space-between;
  ${index === 0 && 'padding-bottom: 8px; border-bottom: 1px solid #e5e5e5;'}

  @media ${mediaQueries.small.only} {
    gap: 8px;
  }
`;

const titleIconWrapper = (isLongTitleAndCurrency: boolean) => css`
  span {
    font-size: 0.875rem;
    ${isLongTitleAndCurrency && `width:60%;`}

    @media ${mediaQueries.small.only} {
      ${isLongTitleAndCurrency && `width:80%;`}
    }
  }
`;

const priceInfoBtnWrapper = css`
  flex-grow: 2;
  justify-content: space-between;

  @media ${mediaQueries.small.only} {
    flex-flow: column wrap;
  }
`;

const buttonStyles = css`
  height: 48px;
  width: 134px;

  @media ${mediaQueries.small.only} {
    width: 97px;
  }
`;

const priceInfoWrapperStyles = css`
  align-items: flex-end;
  min-width: max-content;
`;

const priceLabelStyles = (isMultipleTickets: boolean) => css`
  ${isMultipleTickets ? 'color: #737373' : 'margin-left: auto;'};
  font-size: 0.875rem;
`;

interface TicketCardV4Props extends TicketV4Props {
  index: number;
  isMultipleTickets: boolean;
  isRyanAirTicketingOrMarketingCarrierPresent: boolean;
}

export const TicketCardV4 = ({
  index,
  isLoading,
  isMultipleTickets,
  isRyanAirTicketingOrMarketingCarrierPresent,
  isVI,
  onClick,
  ticket,
  ticketPriceModalInfo,
}: TicketCardV4Props) => {
  const { t } = useTranslation();
  const [isSelected, setIsSelected] = useState(false);
  const { priceDisplayText, pricePerPerson, ticketTitle, ticketType } = ticket;

  const isLongPriceDisplayTextOrCurrency =
    pricePerPerson.length >= PRICE_MIN_THRESHOLD_LENGTH ||
    priceDisplayText?.length >= PRICE_MIN_THRESHOLD_LENGTH;

  const isLongTitleAndCurrency =
    ticketTitle.length >= TICKET_TITLE_MIN_THRESHOLD_LENGTH && isLongPriceDisplayTextOrCurrency;

  const priceInfoCommonStyle = priceInfoCommonStyles(
    isLongPriceDisplayTextOrCurrency,
    isMultipleTickets,
  );

  const handleClick = (typeOfTicket: TicketType) => {
    setIsSelected(true);
    onClick(typeOfTicket);
  };

  return (
    <Card className={cardStyles} data-testid={`${ticket.ticketType}-card`}>
      <Inline className={wrapperStyles(index)} noWrap spacing={16}>
        <Inline
          className={priceInfoBtnWrapper}
          noWrap
          spacing={{ [breakpoints._0]: 4, [breakpoints._768]: 8 }}
        >
          {ticket && (
            <>
              <Inline
                alignY="center"
                className={titleIconWrapper(isLongTitleAndCurrency)}
                noWrap
                spacing={{ [breakpoints._0]: 4, [breakpoints._768]: 8 }}
              >
                <Text variant={textVariants._16}>{ticketTitle}</Text>
                <TicketPriceInfoWrapper
                  isRyanAirTicketingOrMarketingCarrierPresent={
                    isRyanAirTicketingOrMarketingCarrierPresent
                  }
                  isVI={isVI}
                  ticket={ticket}
                  ticketPriceModalInfo={ticketPriceModalInfo}
                />
              </Inline>
              <TicketPriceInfo
                className={cx(priceInfoWrapperStyles, priceInfoCommonStyle)}
                isMultipleTickets={isMultipleTickets}
                isRyanAirTicketingOrMarketingCarrierPresent={
                  isRyanAirTicketingOrMarketingCarrierPresent
                }
                isVI={isVI}
                priceLabelStyle={priceLabelStyles(isMultipleTickets)}
                ticket={ticket}
                ticketPriceModalInfo={ticketPriceModalInfo}
                ticketVersion={4}
              />
            </>
          )}
        </Inline>
        <Inline>
          <Button
            className={buttonStyles}
            data-testid={`${ticketType}-button`}
            isDisabled={isLoading && !isSelected}
            onClick={() => handleClick(ticketType)}
            size="small"
            variant={
              ticketType === TICKET_TYPES.STANDARD_TICKET ? variants.SECONDARY : variants.PRIMARY
            }
          >
            {isLoading && isSelected ? <LoadingDots /> : t('Result.Page.ContinueBooking.Text')}
          </Button>
        </Inline>
      </Inline>
    </Card>
  );
};
